import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { LoaderCircle, X } from 'lucide-react'

export function Button({ children, variant = 'primary', loading, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  loading?: boolean
}) {
  return (
    <button className={`button button-${variant} ${className}`.trim()} disabled={loading || props.disabled} {...props}>
      {loading ? <LoaderCircle size={16} className="spin" /> : null}
      {children}
    </button>
  )
}

export function Field({ label, hint, error, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} />
      {error ? <small className="field-error">{error}</small> : hint ? <small>{hint}</small> : null}
    </label>
  )
}

export function SelectField({ label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select {...props}>{children}</select>
    </label>
  )
}

export function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    NEW: 'Novo', NEEDS_REVIEW: 'Revisar', READY: 'Pronto', BATCHED: 'Em remessa', ROUTED: 'Roteirizado',
    OUT_FOR_DELIVERY: 'Em entrega', DELIVERED: 'Entregue', NOT_FOUND: 'Não encontrado', CANCELLED: 'Cancelado',
    DRAFT: 'Rascunho', PLANNING: 'Planejamento', RELEASED: 'Liberada', IN_PROGRESS: 'Em andamento', COMPLETED: 'Concluída',
    PENDING: 'Pendente', GEOCODED: 'Localizado', APPROXIMATE: 'Aproximado', FAILED: 'Falhou',
    PREVIEW: 'Prévia', RUNNING: 'Processando',
  }
  return <span className={`status status-${value.toLowerCase()}`}>{labels[value] ?? value}</span>
}

export function Modal({ title, children, onClose, size = 'default' }: { title: string; children: ReactNode; onClose: () => void; size?: 'default' | 'wide' }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Fechar"><X size={18} /></button></header>
        {children}
      </section>
    </div>
  )
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="empty"><strong>{title}</strong><p>{description}</p>{action}</div>
}
